import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { FormattedMessage } from "react-intl";
import { APPLICANT_TYPES } from "constants/index";
import { UPDATE_APPLICANT_STATE } from "graphql/mutations";
import { GET_MISSION_ROLE_APPLICANTS } from "graphql/queries";
import { useAuth, useToast } from "hooks";

export default function useApplicantBoardDnd({ setColumns }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const { toast } = useToast();
  const { canMoveApplicants } = useAuth();
  const [updateApplicantState] = useMutation(UPDATE_APPLICANT_STATE, {
    refetchQueries: [GET_MISSION_ROLE_APPLICANTS],
  });
  const [activeApplicant, setActiveApplicant] = useState(null);
  const [notSelectedApplicant, setNotSelectedApplicant] = useState(null);

  const moveApplicant = (applicant, nextState) => {
    setColumns((prev) => {
      const withoutApplicant = Object.keys(prev).reduce(
        (acc, state) => ({
          ...acc,
          [state]: prev[state].filter((item) => item.id !== applicant.id),
        }),
        {}
      );

      return {
        ...withoutApplicant,
        [nextState]: [{ ...applicant, state: nextState }, ...(withoutApplicant[nextState] || [])],
      };
    });
  };

  const handleDragStart = ({ active }) => setActiveApplicant(active.data.current?.applicant);

  const handleDragCancel = () => setActiveApplicant(null);

  const handleDragEnd = ({ active, over, validColumnIds }) => {
    setActiveApplicant(null);
    // The board can be read-only: entitled to APPLICANT_BOARD but not MOVE_APPLICANTS.
    if (!canMoveApplicants || !over) return;

    const applicant = active.data.current?.applicant;
    const nextState = over.id;

    if (!applicant || !validColumnIds.includes(nextState) || applicant.state === nextState) return;

    if (nextState === APPLICANT_TYPES.unselected) {
      setNotSelectedApplicant(applicant);
      return;
    }

    const previousState = applicant.state;

    moveApplicant(applicant, nextState);
    updateApplicantState({
      variables: {
        input: {
          id: applicant.id,
          state: nextState,
        },
      },
      onError: () => {
        moveApplicant({ ...applicant, state: nextState }, previousState);
        toast.error(<FormattedMessage id="Missions.Applicants.ApplicantAdvancedOptions.Error" />);
      },
    });
    toast.success(<FormattedMessage id="Missions.Applicants.Board.MoveSuccess" defaultMessage="Applicant moved" />);
  };

  const handleNotSelectedToastMessages = (callback, ...args) => {
    if (notSelectedApplicant) {
      moveApplicant(notSelectedApplicant, APPLICANT_TYPES.unselected);
    }

    callback?.(...args);
  };

  return {
    sensors,
    activeApplicant,
    notSelectedApplicant,
    setNotSelectedApplicant,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
    handleNotSelectedToastMessages,
  };
}
